import { MenuOutlined, WarningOutlined } from '@ant-design/icons';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Alert, Button, ConfigProvider, Drawer, Layout } from 'antd';
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { Outlet, Route, BrowserRouter as Router, Routes } from 'react-router';

import styles from './App.module.css';

import { createAppRoutes } from './_shared/AppRoutes';
import OutOfDateBanner from './_shared/OutOfDateBanner';
import RouteTelemetry from './_shared/RouteTelemetry';
import { Spinner } from './_shared/Spinner';
import { suspendedLazyComponent } from './_shared/views/SuspendedLazyComponent';
import useResponsive from './hooks/use-responsive';
import { AppThemeContext, useThemeContextValue } from './hooks/use-theme';
import AppFooter from './pages/app-footer/AppFooter';
import { useDocumentation } from './pages/documentation/documentation-hooks';
import { MobileHeader } from './pages/mobile-header/MobileHeader';
import { UsageWarningScreen } from './pages/usage/views/UsageWarningScreen';
import { useUserInfo } from './pages/user-info/user-info-hook';
import { SignedInUser } from './services/auth';
import SideNav from './side-nav/SideNav';
import { useNotifications } from './side-nav/controls/notifications/notifications-hooks';
import { NotificationRelatedItemDispatcher } from './side-nav/controls/notifications/views/NotificationRelatedItemDispatcher';
import {
  APPLICATIONS_ITEM_KEY,
  APPLICATIONS_ITEM_KEY_PREFIX,
  BELLPORT_MESSAGE_MAPPING_KEY,
  DASHBOARDS_ITEM_KEY,
  DASHBOARDS_ITEM_KEY_PREFIX,
  DATA_QUERY_KEY,
  DOCUMENTATION_KEY,
  EXT_APPS_KEY,
  FILE_EXPLORER_KEY,
  INSTANCES_ITEM_KEY,
  INSTANCE_ITEM_KEY_PREFIX,
  JOBS_ITEM_KEY,
  JOBS_ITEM_KEY_PREFIX,
  MONITORING_APPS_KEY_PREFIX,
  MY_DATA_ACTIVITY_KEY,
  NOTIFICATIONS_ITEM_KEY,
  PUBLISHED_MANIFESTS_KEY,
  RAW_FEED_DATA_KEY,
  RELEASE_HISTORY_KEY,
  RESOURCES_ITEM_KEY,
  SPRINGBOARD_BROWSER_KEY,
  SUPERSET_VISUALISATIONS_KEY,
  WORKFLOWS_KEY,
} from './side-nav/sidenav-constants';
import { useAntDTheme } from './utils/antd-theme';
import versionInfo from './version-info';

const AiModal = suspendedLazyComponent(lazy(() => import('./pages/ai/views/AiModal')));

const { Header, Content } = Layout;
const { ErrorBoundary } = Alert;

const queryClient = new QueryClient();

export function App({ user }: { user: SignedInUser }) {
  const themeContextValue = useThemeContextValue();

  const antdTheme = useAntDTheme(themeContextValue.theme);

  const { isMobile, windowSize } = useResponsive();

  const showAiControl = window.CONFIG.featureFlags.enableBedrockDocumentation;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { userInfoModel, userInfoDelegate } = useUserInfo();

  // We are currently not using the return values from these hooks.
  // But we must call them here to instantiate controller and fetch data when App loads.
  useNotifications();
  useDocumentation();

  useEffect(() => {
    void userInfoDelegate?.onAppStartedWithUser();
  }, [userInfoDelegate]);

  const toggleMobileMenuOpen = useCallback(() => {
    setMobileMenuOpen((open) => !open);
  }, []);

  const handleSideNavMenuItemClick = (menuKey: string) => {
    if (
      menuKey === DASHBOARDS_ITEM_KEY ||
      menuKey.startsWith(DASHBOARDS_ITEM_KEY_PREFIX) ||
      menuKey.startsWith(APPLICATIONS_ITEM_KEY_PREFIX) ||
      menuKey === APPLICATIONS_ITEM_KEY ||
      menuKey === JOBS_ITEM_KEY ||
      menuKey.startsWith(JOBS_ITEM_KEY_PREFIX) ||
      menuKey === PUBLISHED_MANIFESTS_KEY ||
      menuKey === INSTANCES_ITEM_KEY ||
      menuKey.startsWith(INSTANCE_ITEM_KEY_PREFIX) ||
      menuKey === RESOURCES_ITEM_KEY ||
      menuKey === RELEASE_HISTORY_KEY ||
      menuKey === BELLPORT_MESSAGE_MAPPING_KEY ||
      menuKey === FILE_EXPLORER_KEY ||
      menuKey === EXT_APPS_KEY ||
      menuKey === NOTIFICATIONS_ITEM_KEY ||
      menuKey.startsWith(MONITORING_APPS_KEY_PREFIX) ||
      menuKey === SPRINGBOARD_BROWSER_KEY ||
      menuKey === SUPERSET_VISUALISATIONS_KEY ||
      menuKey.startsWith(DOCUMENTATION_KEY) ||
      menuKey === DATA_QUERY_KEY ||
      menuKey === WORKFLOWS_KEY ||
      menuKey === MY_DATA_ACTIVITY_KEY ||
      menuKey === RAW_FEED_DATA_KEY
    ) {
      setMobileMenuOpen(false);
    }
  };

  const loadingElement = <Spinner />;
  if (userInfoModel.loading) {
    return loadingElement;
  }

  const mobileHeader = (
    <Header className={`${styles.appHeader} ${styles.appMobileHeader} mobile-header`}>
      <NotificationRelatedItemDispatcher />
      <Drawer
        title={window.CONFIG.branding.longName}
        closable
        onClose={() => setMobileMenuOpen(false)}
        placement="left"
        open={mobileMenuOpen}
        width={windowSize.width}
        rootStyle={{ padding: '0' }}
      >
        {mobileMenuOpen && <SideNav user={user} onMenuItemClick={handleSideNavMenuItemClick} />}
      </Drawer>
      <Button onClick={toggleMobileMenuOpen}>
        <MenuOutlined className="header-menu-btn" />
      </Button>
      <MobileHeader />
    </Header>
  );

  const notFoundContent = (
    <>
      <WarningOutlined style={{ fontSize: '48px', margin: '0 auto 1rem', display: 'block' }} />
      <h1>404 - Page not found</h1>
      <p>The page you are looking for does not exist.</p>
      <a href="/">Go to homepage</a>
    </>
  );

  const forbiddenContent = (
    <>
      <WarningOutlined style={{ fontSize: '48px', margin: '0 auto 1rem', display: 'block' }} />
      <h1>403 - Forbidden</h1>
      <p>
        You do not have permission to access this page. If you believe you should have access, please clear your browser data and try again.
      </p>
      <a href="/">Go to homepage</a>
    </>
  );

  const errorBoundaryMessage = 'Sorry, the child page has encountered an error; please select another page or refresh the app.';

  // Warning: trying to move the desktop and mobile elements below to their own component (instead of inline)
  // causes their model and delegate to be undefined...
  return (
    <QueryClientProvider client={queryClient}>
      <AppThemeContext.Provider value={themeContextValue}>
        <ConfigProvider theme={antdTheme}>
          {userInfoModel.workbenchAllowed && <UsageWarningScreen />}
          <Router>
            <RouteTelemetry />
            <Suspense fallback={<div>Loading&#8230;</div>}>
              <Routes>
                {userInfoModel.checkInFailed ? (
                  <>
                    <Route
                      path="/403"
                      element={
                        <Layout
                          style={{
                            height: '100vh',
                          }}
                        >
                          <Content className={styles.notFoundContent}>{forbiddenContent}</Content>
                          {isMobile && <AppFooter localVersionInfo={versionInfo} />}
                        </Layout>
                      }
                    />
                    <Route
                      path="*"
                      element={
                        <Layout
                          style={{
                            height: '100vh',
                          }}
                        >
                          <Content className={styles.notFoundContent}>{forbiddenContent}</Content>
                          {isMobile && <AppFooter localVersionInfo={versionInfo} />}
                        </Layout>
                      }
                    />
                  </>
                ) : (
                  <>
                    {!isMobile && (
                      <Route
                        path="/"
                        element={
                          <div className="app-background">
                            <div className="pwa-header">{window.CONFIG.branding.longName}</div>
                            <NotificationRelatedItemDispatcher />
                            <OutOfDateBanner />
                            <Layout>
                              <SideNav user={user} />
                              <Content className="secondary-sidebar-hidden">
                                <ErrorBoundary message={errorBoundaryMessage}>
                                  <Outlet />
                                </ErrorBoundary>
                                {showAiControl && <AiModal />}
                              </Content>
                            </Layout>
                          </div>
                        }
                      >
                        {createAppRoutes(userInfoModel)}
                      </Route>
                    )}

                    {isMobile && (
                      <Route
                        path="/"
                        element={
                          <Layout className="app-background">
                            {mobileHeader}
                            <Content
                              className="text-center"
                              style={{
                                display: mobileMenuOpen ? 'none' : 'block',
                              }}
                            >
                              <ErrorBoundary message={errorBoundaryMessage}>
                                <Outlet />
                              </ErrorBoundary>
                            </Content>
                            {<AppFooter localVersionInfo={versionInfo} />}
                          </Layout>
                        }
                      >
                        {createAppRoutes(userInfoModel)}
                      </Route>
                    )}

                    <Route
                      path="*"
                      element={
                        <Layout
                          style={{
                            height: '100vh',
                          }}
                        >
                          {isMobile && mobileHeader}
                          {!isMobile && <SideNav user={user} />}
                          <Content
                            className={styles.notFoundContent}
                            style={{
                              display: mobileMenuOpen ? 'none' : 'flex',
                            }}
                          >
                            {notFoundContent}
                          </Content>
                          {isMobile && <AppFooter localVersionInfo={versionInfo} />}
                        </Layout>
                      }
                    />
                  </>
                )}
              </Routes>
            </Suspense>
          </Router>
        </ConfigProvider>
      </AppThemeContext.Provider>
    </QueryClientProvider>
  );
}
